<template>
    <div class="quiz-container">
      <h2 class="quiz-title">{{ quiz.title }}</h2>
      <p class="question-counter">Question {{ currentQuestionIndex + 1 }} of {{ quiz.questions.length }}</p>
      <div class="question-box">
        <h3 class="question">{{ quiz.questions[currentQuestionIndex].question }}</h3>
        <ul class="options-list">
          <li
            v-for="(option, index) in quiz.questions[currentQuestionIndex].options"
            :key="index"
            @click="selectOption(index)"
            :class="{ 'selected': selectedOptionIndex === index, 'correct': isAnswerRevealed && index === quiz.questions[currentQuestionIndex].correctAnswer, 'incorrect': isAnswerRevealed && selectedOptionIndex === index && index !== quiz.questions[currentQuestionIndex].correctAnswer }"
            class="option"
          >
            {{ option }}
            <span v-if="showFeedback && currentQuestion.correctAnswer === index" class="feedback-icon correct">✓</span>
        <span v-if="showFeedback && selectedOption === index && currentQuestion.correctAnswer !== index" class="feedback-icon incorrect">✗</span>
          </li>
        </ul>
      </div>
      <div v-if="isAnswerRevealed" class="feedback">
        <p :class="{ 'feedback-correct': isCorrect, 'feedback-incorrect': !isCorrect }">
          {{ feedbackMessage }}
        </p>
        
      </div>
      <div class="navigation-buttons">
        <button @click="previousQuestion" :disabled="currentQuestionIndex === 0" class="action-button">Back</button>
        <button @click="nextQuestion" :disabled="currentQuestionIndex === quiz.questions.length - 1 || !isAnswerRevealed" class="action-button">Next</button>
      </div>
      <div v-if="currentQuestionIndex === quiz.questions.length - 1 && isAnswerRevealed" class="results-summary">
        <h4>Quiz Summary</h4>
        <p>Correct answers: {{ correctAnswersCount }}</p>
        <p>Incorrect answers: {{ incorrectAnswersCount }}</p>
        <button @click="retakeQuiz" class="action-button">Retake Quiz</button>
        <button @click="goToNextQuiz" class="action-button next-quiz-button">Next Quiz</button>
      </div>
    </div><br><br><br><br><br><br>
  </template>
  
  <script>
import { ref } from 'vue';

export default {
  setup() {
  
    const correctAnswerSound = new Audio('https://assets.codepen.io/11151897/stock-market-bell.mp3');
    const quiz = ref({
      title: "Introduction to Mutual Funds",
      questions: [
        {
          question: "What is a mutual fund?",
          options: [
            "A private fund available only to accredited investors",
            "A pool of money collected from many investors to invest in stocks, bonds, and other assets",
            "A government-issued security",
            "An account that offers fixed interest rates for fixed terms"
          ],
          correctAnswer: 1
        },
        {
          question: "Which of the following is a benefit of investing in mutual funds?",
          options: [
            "Guaranteed profits",
            "Professional management and diversification",
            "No risk of loss",
            "Fixed interest rates"
          ],
          correctAnswer: 1
        },
        {
          question: "How do mutual funds generate returns for investors?",
          options: [
            "Through fixed monthly payments",
            "By betting on currency fluctuations",
            "By investing in a diversified portfolio of assets and distributing the profits",
            "By locking in funds for a fixed term at a fixed rate"
          ],
          correctAnswer: 2
        },
        {
          question: "What is the NAV (Net Asset Value) of a mutual fund?",
          options: [
            "The total debt the fund owes",
            "The price at which shares of the fund can be bought or sold",
            "The fund's yearly profit margin",
            "The initial investment required to join the fund"
          ],
          correctAnswer: 1
        },
        {
          question: "What are 'load' fees in mutual funds?",
          options: [
            "Annual management fees",
            "Fees charged for buying or selling shares in the fund",
            "Interest charged on the fund's borrowed money",
            "Taxes imposed by the government on mutual fund profits"
          ],
          correctAnswer: 1
        },
        {
          question: "What does it mean if a mutual fund is 'actively managed'?",
          options: [
            "The fund managers make regular investment decisions in an attempt to outperform the market",
            "Investors must actively participate in voting on fund decisions",
            "The fund automatically adjusts its portfolio based on market conditions",
            "It involves frequent buying and selling of stocks within the fund"
          ],
          correctAnswer: 0
        },
        {
          question: "How can mutual funds offer diversification?",
          options: [
            "By investing only in stocks",
            "By focusing on a single sector or industry",
            "By spreading investments across various asset classes, reducing risk",
            "By pooling funds from a diverse group of investors"
          ],
          correctAnswer: 2
        },
        {
          question: "What is a 'bond mutual fund'?",
          options: [
            "A fund that exclusively invests in real estate",
            "A fund that lends money to investors to buy bonds",
            "A fund that invests primarily in bonds and other debt instruments",
            "A fund that guarantees a fixed return to its investors"
          ],
          correctAnswer: 2
        },
        {
          question: "What is the difference between an 'index fund' and an 'actively managed mutual fund'?",
          options: [
            "Index funds attempt to replicate the performance of a specific market index, while actively managed funds aim to outperform the market",
            "Index funds are not regulated, whereas actively managed funds are",
            "Index funds can only invest in stocks, but actively managed funds can invest in any type of asset",
            "There is no difference; the terms are interchangeable"
          ],
          correctAnswer: 0
        },
        {
          question: "Why might an investor choose a mutual fund over individual stocks?",
          options: [
            "Mutual funds offer a fixed rate of return",
            "Mutual funds allow direct control over the choice of investments",
            "Mutual funds provide professional management and diversification, potentially reducing risk",
            "Investing in mutual funds requires no initial investment"
          ],
          correctAnswer: 2
        }
      ]
    });
    const currentQuestionIndex = ref(0);
    const correctAnswersCount = ref(0);
    const incorrectAnswersCount = ref(0);
    const selectedOptionIndex = ref(-1);
    const isAnswerRevealed = ref(false);
    const isCorrect = ref(false);
    const feedbackMessage = ref("");

    const selectOption = (index) => {
      if (isAnswerRevealed.value) return;
      selectedOptionIndex.value = index;
      isAnswerRevealed.value = true;

      if (index === quiz.value.questions[currentQuestionIndex.value].correctAnswer) {
        isCorrect.value = true;
        correctAnswersCount.value++;
        feedbackMessage.value = "Correct!";
        correctAnswerSound.play().catch(error => console.error("Audio play failed", error));
      } else {
        isCorrect.value = false;
        incorrectAnswersCount.value++;
        feedbackMessage.value = "Incorrect.";
      }
    };

    const nextQuestion = () => {
      if (currentQuestionIndex.value < quiz.value.questions.length - 1) {
        currentQuestionIndex.value++;
        resetState();
      }
    };

    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        currentQuestionIndex.value--;
        resetState();
      }
    };

    const retakeQuiz = () => {
      currentQuestionIndex.value = 0;
      correctAnswersCount.value = 0;
      incorrectAnswersCount.value = 0;
      resetState();
    };

    const resetState = () => {
      selectedOptionIndex.value = -1;
      isAnswerRevealed.value = false;
      isCorrect.value = false;
      feedbackMessage.value = "";
    };

    return {
      quiz,
      currentQuestionIndex,
      correctAnswersCount,
      incorrectAnswersCount,
      selectedOptionIndex,
      isAnswerRevealed,
      isCorrect,
      feedbackMessage,
      selectOption,
      nextQuestion,
      previousQuestion,
      retakeQuiz,
    };
  },
};
</script>

<style scoped>
.quiz-container {
  color: #FFF;
  padding: 20px;
  border-radius: 10px;
  max-width: 600px;
  margin: auto;
  background: linear-gradient(135deg, #6D5BBA 0%, #8D58BF 100%);
}


.feedback-correct, .feedback-incorrect {
  padding: 5px 10px; /* Adds some padding around the text */
  border-radius: 15px; /* Rounds the corners for a bubble-like appearance */
  display: inline-block; /* Makes the background wrap the text */
  color: #FFF; /* Keeps text color white for high contrast */
  font-weight: bold; /* Ensure the text is bold */
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.7); /* Optional: Adds a slight shadow for depth */
}

.feedback-correct {
  background-color: #4CAF50; /* Green background */
  border: 2px solid #FFF; /* White border for contrast */
  color: fff;
}

.feedback-incorrect {
  background-color: #F44336; /* Red background */
  border: 2px solid #FFF; /* White border for contrast */
}
.quiz-title, .question-counter, .question, .feedback {
  text-align: center;
  font-size: 1rem;
}

.results-summary {
  padding: 20px; /* Adds padding inside the summary box */
  background-color: rgba(255, 255, 255, 0.9); /* Semi-transparent white background */
  border-radius: 15px; /* Rounded corners */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
  margin-top: 20px; /* Space above the summary */
  border: 2px solid #DDD; /* Light border for definition */
}

.results-summary h4 {
  color: #333; /* Darker text for contrast against light background */
  font-size: 1.5rem; /* Larger font size for the title */
}

.results-summary p {
  color: #555; /* Subtle text color for readability */
  font-size: 1rem; /* Larger font size for the content */
}

.options-list {
  list-style-type: none;
  padding: 0;
}

.option {
  margin: 10px 0;
  padding: 10px;
  color: black;
  background-color: #f4f4f8;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 1rem;
}

.option:hover {
  background-color: #d0ccd3;
}

.option.selected.correct {
  border-color: green;
}

.option.selected.incorrect {
  border-color: red;
}
.button-container {
  display: flex;
  justify-content: center; /* Center the buttons horizontally */
  margin-top: 20px; /* Space above the button container */
}
.action-button {
  background-color: #4a148C; 
  color: #DDD;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 25px;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;
  background-color: #36454f; /* Black background */
  color: white;
  justify-content: center; 
}


.action-button:hover {
  background-color: black; /* Dark purple background on hover */
}


.next-quiz-button {
  background-color: #4CAF50; /* Example green background */
  color: white;
  /* Add other styles as needed */
}

.centered-title {
  text-align: center;
  font-size: 2rem; /* Increase font size */
  color: #36454f; /* Dark text for contrast */
  margin-bottom: 20px; /* Add some space below the title */
}

.quiz-container h1 {
  font-size: 1.5rem; /* Make questions stand out */
  color: #4A148C; /* Use the dark purple for questions */
  margin-bottom: 15px;
}

.option-card {
  background-color: #f4f4f8;
  border: 1px solid #ccc;
  padding: 10px;
  margin: 5px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color .3s, transform .2s;
}

.option-card:hover {
  background-color: #e9e9ef;
  transform: scale(1.02); /* Slightly enlarge options on hover */
}

.feedback-icon {
  font-weight: bold;
  margin-left: 15px;
}

.correct {
  color: green;
}

.incorrect {
  color: red;
}

.feedback {
  margin-top: 20px;
  text-align: center;
}

</style>